import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, User, Phone, Mail, MessageSquare } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    date: new Date(),
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateSelect = (date) => {
    setFormData({
      ...formData,
      date: date
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Format date as YYYY-MM-DD
      const formattedDate = formData.date.toISOString().split('T')[0];
      
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.service_type,
        date: formattedDate,
        time: formData.time,
        message: formData.message
      };

      const response = await axios.post(`${BACKEND_URL}/api/bookings`, bookingData);
      
      if (response.data) {
        toast.success('Booking request submitted successfully! Check your email for confirmation.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: '',
          date: new Date(),
          time: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try calling us directly at 067 493 3211.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="section-padding bg-page">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Book an Appointment</h2>
          <p className="body-large max-w-3xl mx-auto">
            Schedule your home visit or virtual consultation.<br />
            Available Monday to Sunday, 9am-6pm.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <div className="booking-calendar-section">
              <h3 className="heading-3 mb-4">Select Date</h3>
              <div className="calendar-container">
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date()}
                  className="booking-calendar"
                />
              </div>
              
              <div className="mt-6 glass-card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Clock size={20} className="text-medical-red" />
                  <span className="heading-3">Operating Hours</span>
                </div>
                <p className="body-medium">Monday - Sunday<br />9:00 AM - 6:00 PM</p>
              </div>
            </div>

            {/* Booking Form */}
            <div className="booking-form-section">
              <h3 className="heading-3 mb-4">Your Details</h3>
              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <User size={16} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <Phone size={16} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="067 123 4567"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <Mail size={16} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service_type" className="form-label">
                    <CalendarIcon size={16} />
                    Service Type *
                  </label>
                  <select
                    id="service_type"
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select a service</option>
                    <option value="home-visit">Home Visit (R550)</option>
                    <option value="virtual">Virtual Consultation (R200)</option>
                    <option value="lab">Lab Workup (R100)</option>
                    <option value="followup">Follow-Up Appointment (R400)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="time" className="form-label">
                    <Clock size={16} />
                    Preferred Time *
                  </label>
                  <select
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select a time</option>
                    <option value="09:00">09:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="12:00">12:00 PM</option>
                    <option value="13:00">01:00 PM</option>
                    <option value="14:00">02:00 PM</option>
                    <option value="15:00">03:00 PM</option>
                    <option value="16:00">04:00 PM</option>
                    <option value="17:00">05:00 PM</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <MessageSquare size={16} />
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="form-input"
                    placeholder="Tell us about your medical concern or any specific requirements..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
                </button>

                <p className="body-small text-center mt-4 text-muted">
                  You will receive confirmation via email or WhatsApp with payment details.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
